import React from "react";
import { TablePagination } from "../components/table";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Box,
  Card,
  CardContent,
  Typography,
  TableContainer,
} from "@mui/material";
import * as api from "../services/api";
import { useLoaderData, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import * as qs from "../qs";
import * as p from "../parser";
import { createClientFilter } from "../client";
import { debounce } from "shared/src/util.mjs";
import { DEBOUNCE_INPUT } from "../constants";
import { useNavigate } from "../url";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import * as f from "../utils/formatter.js";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../context/AuthenticationTokenContext.jsx";

const defaults = [
  [["page", "limit"], 25],
  [["page", "offset"], 0],
  [["filter", "current_company"], { eq: true }],
];

const parsers = [
  [["page", "limit"], (val) => p.parseInt(val, 10)],
  [["page", "offset"], (val) => p.parseInt(val, 0)],
];

const filters = [[["filter", "search"], (v) => v.length === 0]];

function graphqlArgs(request) {
  const parsedArgs = qs.parse(new URL(request.url).searchParams.toString(), {
    defaults: defaults,
    parsers: parsers,
    filters: filters,
  });

  const search = parsedArgs.filter?.search;
  if (search != null) {
    const clientFilter = createClientFilter(search);
    if (clientFilter == null) {
      return null;
    }

    delete parsedArgs.filter.search;
    parsedArgs.filter = {
      ...parsedArgs.filter,
      or: [...(parsedArgs.filter.or ?? []), clientFilter],
    };
  }

  return parsedArgs;
}

export async function loader({ request }) {
  const args = graphqlArgs(request);
  if (args == null) {
    return { clients: { data: { clients: { data: [] } } } };
  }

  return {
    clients: await api.loadClients(args, api.contextFromRequest(request)),
  };
}

function useChangeQuery() {
  const [_, setSearchParams] = useSearchParams();
  const changeQuery = React.useCallback(
    (newValues) => {
      setSearchParams((prev) =>
        Object.entries({ ...Object.fromEntries(prev), ...newValues }).filter(
          ([_, v]) => v !== "",
        ),
      );
    },
    [setSearchParams],
  );

  return changeQuery;
}

function useSearch() {
  const changeQuery = useChangeQuery();

  return React.useCallback(
    debounce(
      (term) =>
        changeQuery({
          "page[offset]": 0,
          "filter[search]": term,
        }),
      DEBOUNCE_INPUT,
    ),
    [changeQuery],
  );
}

export default function Clients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loaderData = useLoaderData();
  const clientsConnection = loaderData.clients.data?.clients;
  const search = useSearch();
  const [searchParams] = useSearchParams();
  const roles = useRoles();
  const canSeeBirthInfo = isGrantedAny(
    roles,
    new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h2">{t("clients.clients")}</Typography>
          <TextField
            type="search"
            variant="outlined"
            size="small"
            defaultValue={searchParams.get("filter[search]") ?? ""}
            onInput={(e) => search(e.target.value)}
            placeholder={t("reception.search")}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "divider",
                },
                "& input::placeholder": {
                  opacity: 0.9,
                  color: "var(--mui-palette-_components-heading-color)",
                },
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
        <TableContainer sx={{ maxHeight: "75vh" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("clients.firstName")}</TableCell>
                <TableCell>{t("clients.lastName")}</TableCell>
                {canSeeBirthInfo && (
                  <>
                    <TableCell>{t("client.birthNumber")}</TableCell>
                    <TableCell>{t("client.birthDate")}</TableCell>
                  </>
                )}
                <TableCell>{t("client.location")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientsConnection?.data.map((row) => (
                <TableRow
                  key={row.id}
                  hover={true}
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/clients/${row.id}`)}
                >
                  <TableCell>{row.first_name}</TableCell>
                  <TableCell>{row.last_name}</TableCell>
                  {canSeeBirthInfo && (
                    <>
                      <TableCell>{row.birth_number}</TableCell>
                      <TableCell>{f.date(row.birthdate)}</TableCell>
                    </>
                  )}
                  <TableCell>{row.location?.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <TablePagination connection={clientsConnection} component="div" />
        </Box>
      </CardContent>
    </Card>
  );
}
